import { z } from 'zod'

/**
 * Where the subject of an image is, as fractions of its width and height.
 *
 * `{ x: 0.5, y: 0.5 }` is the center; `{ x: 0.5, y: 0.2 }` is centered
 * horizontally and a fifth of the way down. It exists because one image is shown
 * at several shapes — a venue cover is 16:10 on a phone and 3:1 on a desktop, a
 * space photograph is 4:3 in its editor and a small thumbnail in a list — and
 * cropping to the middle cuts the top off a room whose subject sits high in frame.
 *
 * A point rather than a region, which is the pattern every CMS settled on (Sanity's
 * hotspot, Drupal's Focal Point, imgix's `fp-x`/`fp-y`, Cloudinary's gravity). The
 * limitation is real: a point cannot say "keep this whole archway visible". It is
 * enough for photographs of rooms, and it needs no image processing at all —
 * `object-fit: cover` plus `object-position` does the work in the browser.
 */
export const focalPointSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
})

export type FocalPoint = z.infer<typeof focalPointSchema>

/** The middle, for callers that need a value rather than an absence. */
const CENTER_FOCAL_POINT: FocalPoint = { x: 0.5, y: 0.5 }

/**
 * A focal point as a CSS `object-position`.
 *
 * `fallback` is what an image nobody has focused should use — not necessarily the
 * center. The venue hero has long drawn covers slightly above center because that
 * is where the subject of a room photograph usually sits, and moving those to dead
 * center the day focal points shipped would have been a visible regression on every
 * existing cover.
 */
export function objectPositionOf(
  focal: FocalPoint | null | undefined,
  fallback = '50% 50%',
): string {
  if (!focal) return fallback
  return `${String(round(focal.x * 100))}% ${String(round(focal.y * 100))}%`
}

/**
 * The inverse of {@link objectPositionOf}: a `N% N%` string as a point inside the image.
 *
 * A keyword, a single percentage, or a value past the edge is the center. The
 * picker draws its crosshair from this, and an unvalidated fallback must not
 * become a point outside the photograph.
 */
export function focalPointFromObjectPosition(value: string): FocalPoint {
  const found = /^(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%$/.exec(value.trim())
  const x = found?.[1]
  const y = found?.[2]
  if (x === undefined || y === undefined) return CENTER_FOCAL_POINT
  const parsed = focalPointSchema.safeParse({ x: Number(x) / 100, y: Number(y) / 100 })
  return parsed.success ? parsed.data : CENTER_FOCAL_POINT
}

/** Two decimal places — enough to be exact on any screen, short enough to read. */
function round(n: number): number {
  return Math.round(n * 100) / 100
}

/**
 * Build a focal point from two nullable columns.
 *
 * Both or neither: half a focal point is not a position, and the check constraints
 * on the tables enforce the same rule. A row that somehow holds one is read as
 * having none rather than as having a point at zero. A pair outside the image
 * is the same absence: the schema, not the column types, is what makes a point.
 */
export function focalPointFrom(x: number | null, y: number | null): FocalPoint | null {
  if (x == null || y == null) return null
  const parsed = focalPointSchema.safeParse({ x, y })
  return parsed.success ? parsed.data : null
}
